import type { ChatMessageAttachment } from "@albertine/types/openapi/concierge-v1";

const SNIFF_BYTE_COUNT = 16;

const HEIF_BRANDS = new Set(["heic", "heix", "heif", "heim", "heis", "mif1", "msf1"]);

export async function fileToChatAttachment(
    file: File,
): Promise<Extract<ChatMessageAttachment, { type: "file" }>> {
    return {
        type: "file",
        url: URL.createObjectURL(file),
        mimeType: await resolveFileMimeType(file),
        filename: file.name,
    };
}

export async function resolveFileMimeType(file: File): Promise<string> {
    if (file.type && file.type !== "application/octet-stream") {
        return file.type;
    }

    return (await sniffMimeTypeFromFile(file)) ?? "application/octet-stream";
}

export function mimeTypeFromFileHeader(header: Uint8Array): string | null {
    return sniffMimeType(header);
}

async function sniffMimeTypeFromFile(file: File): Promise<string | null> {
    const header = await readFileHeader(file, SNIFF_BYTE_COUNT);
    return sniffMimeType(header);
}

function sniffMimeType(header: Uint8Array): string | null {
    if (hasPrefix(header, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        return "image/png";
    }

    if (hasPrefix(header, [0xff, 0xd8, 0xff])) {
        return "image/jpeg";
    }

    if (
        hasPrefix(header, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
        hasPrefix(header, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61])
    ) {
        return "image/gif";
    }

    if (
        hasPrefix(header, [0x52, 0x49, 0x46, 0x46]) &&
        hasBytesAt(header, 8, [0x57, 0x45, 0x42, 0x50])
    ) {
        return "image/webp";
    }

    if (hasPrefix(header, [0x42, 0x4d])) {
        return "image/bmp";
    }

    if (hasBytesAt(header, 4, [0x66, 0x74, 0x79, 0x70]) && isHeifBrand(header)) {
        return "image/heic";
    }

    if (hasPrefix(header, [0x25, 0x50, 0x44, 0x46])) {
        return "application/pdf";
    }

    return null;
}

function isHeifBrand(header: Uint8Array): boolean {
    if (header.length < 12) {
        return false;
    }

    const brand = String.fromCharCode(header[8], header[9], header[10], header[11]);
    return HEIF_BRANDS.has(brand);
}

async function readFileHeader(file: File, byteCount: number): Promise<Uint8Array> {
    const blob = file.slice(0, byteCount);

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(new Uint8Array(reader.result as ArrayBuffer));
        };
        reader.onerror = () => {
            reject(reader.error ?? new Error("Failed to read file header"));
        };
        reader.readAsArrayBuffer(blob);
    });
}

function hasPrefix(bytes: Uint8Array, prefix: readonly number[]): boolean {
    return hasBytesAt(bytes, 0, prefix);
}

function hasBytesAt(
    bytes: Uint8Array,
    offset: number,
    expected: readonly number[],
): boolean {
    if (bytes.length < offset + expected.length) {
        return false;
    }

    for (let index = 0; index < expected.length; index += 1) {
        if (bytes[offset + index] !== expected[index]) {
            return false;
        }
    }

    return true;
}
